import React, { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, Check, Play, Pause } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const C = {
  cream: "#f5e0ad",
  creamDim: "rgba(245,224,173,0.75)",
  gold: "#e7c98a",
  goldMist: "rgba(231,201,138,0.24)",
};

const sameIds = (a = [], b = []) => a.length === b.length && a.every((x, i) => x === b[i]);

const PreviewBtn = ({ mood, playing, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={`mood-preview-${mood}`}
    aria-label={playing ? "Stop preview" : "Play 10-second preview"}
    className="absolute right-3 bottom-3 inline-flex items-center justify-center rounded-full transition-[background-color,transform] duration-200 active:scale-95"
    style={{ width: 36, height: 36, border: `1px solid ${C.gold}`, background: playing ? C.gold : "rgba(13,20,23,0.5)", color: playing ? "#0d1417" : C.gold }}
  >
    {playing ? <Pause size={14} /> : <Play size={14} style={{ marginLeft: 2 }} />}
  </button>
);

// "The journey name creates emotion; the mood labels create understanding."
export const MoodTags = ({ words, testId }) => (
  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={testId}>
    {(words || []).slice(0, 5).map((w) => (
      <span key={w} className="rounded-full px-2.5 py-0.5 text-[0.62rem] tracking-[0.08em]"
        style={{ border: `1px solid ${C.goldMist}`, color: C.creamDim, fontFamily: '"Manrope", sans-serif' }}>{w}</span>
    ))}
  </div>
);

const ChoiceCard = ({ c, active, current, onPick, playing, onPreview }) => (
  <div className="relative">
  <button
    type="button"
    onClick={onPick}
    data-testid={`mood-choice-${c.mood}`}
    aria-pressed={active}
    className="w-full text-left pl-4 pr-14 py-3 rounded-xl transition-[background-color,border-color,transform] duration-200 active:scale-[0.99]"
    style={{
      background: active ? "linear-gradient(135deg, rgba(215,187,122,0.22) 0%, rgba(215,187,122,0.08) 100%)" : "transparent",
      border: `1px solid ${active ? C.gold : C.goldMist}`,
    }}
  >
    <div className="flex items-center justify-between gap-2">
      {c.soulumo_selected || current ? (
        <span className="inline-flex items-center gap-2">
        {c.soulumo_selected && <span className="inline-flex items-center gap-1 uppercase tracking-[0.22em] text-[0.6rem]" style={{ color: C.gold, fontFamily: '"Manrope", sans-serif' }} data-testid="mood-choice-soulumo-selected">
          <Sparkles size={11} /> Soulumo Selected
        </span>}
        {current && <span className="uppercase tracking-[0.22em] text-[0.6rem]" style={{ color: C.cream, fontFamily: '"Manrope", sans-serif' }} data-testid={`mood-choice-${c.mood}-current`}>· Current music</span>}
        </span>
      ) : <span />}
      {active && <Check size={15} color={C.gold} data-testid={`mood-choice-${c.mood}-active`} />}
    </div>
    <p style={{ color: C.cream, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.15rem" }}>{c.label}</p>
    <MoodTags words={c.feelings} testId={`mood-feelings-${c.mood}`} />
  </button>
  <PreviewBtn mood={c.mood} playing={playing} onToggle={onPreview} />
  </div>
);

// 10s sample of a mood's first track, faded in and out.
const PREVIEW_START = 12, PREVIEW_LEN = 10, FADE = 1.2;
function usePreview() {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(null);
  const stop = () => {
    const a = ref.current;
    if (a) { a.pause(); a.ontimeupdate = null; }
    setPlaying(null);
  };
  const toggle = (mood, url) => {
    if (playing === mood) return stop();
    stop();
    const a = ref.current || (ref.current = new Audio());
    a.src = `${API}${url}`;
    a.volume = 0;
    a._start = undefined;
    a.onloadedmetadata = () => {
      a._start = Math.min(PREVIEW_START, Math.max(0, (a.duration || 0) - PREVIEW_LEN));
      a.currentTime = a._start;
    };
    a.ontimeupdate = () => {
      if (a._start === undefined || a.seeking) return;
      const t = a.currentTime - a._start;
      if (t >= PREVIEW_LEN) return stop();
      a.volume = Math.max(0, Math.min(1, t / FADE, (PREVIEW_LEN - t) / FADE));
    };
    a.play().then(() => setPlaying(mood)).catch(stop);
  };
  useEffect(() => () => ref.current && ref.current.pause(), []);
  return { playing, toggle };
}

export const MoodJourneyChoices = ({ theme, concern, selectedIds, onSelect, autoSelect = true, currentIds = null }) => {
  const [choices, setChoices] = useState(null);
  const { playing, toggle } = usePreview();

  useEffect(() => {
    const q = new URLSearchParams({ theme: theme || "", concern: concern || "" });
    fetch(`${API}/api/unified-audio/mood-journey?${q}`)
      .then((r) => r.json())
      .then((d) => {
        const list = d.choices || [];
        setChoices(list);
        if (autoSelect && !(selectedIds || []).length && list[0]) onSelect(list[0].tracks.map((t) => t.id), list[0].mood);
      })
      .catch(() => setChoices([]));
  }, [theme, concern]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!choices) return <Loader2 className="animate-spin" color={C.gold} />;
  return (
    <div className="space-y-2.5 mb-4" data-testid="mood-journey-choices">
      {choices.map((c) => {
        const ids = c.tracks.map((t) => t.id);
        return <ChoiceCard key={c.mood} c={c} active={sameIds(ids, selectedIds)} current={!!currentIds && sameIds(ids, currentIds)} onPick={() => onSelect(ids, c.mood, c)}
          playing={playing === c.mood} onPreview={() => toggle(c.mood, c.tracks[0].url)} />;
      })}
    </div>
  );
};
